import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { API_BASE_URL } from '../config';
import logo from '../assets/images/candid-i-logo.png';
import {
  clampText,
  formatMarketDate,
  handleMarketImageError,
  normalizeMarketIntelligenceItem,
} from './marketIntelligenceUtils';

const renderMedia = (article) => {
  if (!article?.video_url && !article?.media_url && !article?.featured_image) return null;

  const source = article.video_url || article.media_url || article.featured_image;
  const isVideo = (article.media_type || '').toLowerCase() === 'video' || /\.(mp4|webm|mov)(\?|$)/i.test(source);
  const isYouTube = /youtube\.com|youtu\.be/i.test(source);

  if (isVideo && isYouTube) {
    const embedUrl = source.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/');
    return (
      <iframe
        title={article.title}
        src={embedUrl}
        className="h-full w-full rounded-[2rem]"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  if (isVideo) {
    return (
      <video controls className="h-full w-full rounded-[2rem] bg-black object-cover">
        <source src={source} />
        Your browser does not support the video tag.
      </video>
    );
  }

  return (
    <img
      src={source}
      alt={article.featured_image_alt || article.title}
      className="h-full w-full rounded-[2rem] object-cover"
      onError={handleMarketImageError}
    />
  );
};

const MarketIntelligenceArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const [articleResponse, listResponse] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/market-intelligence/articles/slug/${slug}`),
        axios.get(`${API_BASE_URL}/api/market-intelligence/articles`, { params: { page: 1, per_page: 24 } }),
      ]);

      const apiArticle = articleResponse.status === 'fulfilled' ? articleResponse.value.data?.data : null;
      const apiList = listResponse.status === 'fulfilled' ? (listResponse.value.data?.data || []) : [];
      if (!apiArticle) {
        throw new Error('Article not found');
      }
      const normalizedArticle = normalizeMarketIntelligenceItem(apiArticle);
      const normalizedList = apiList
        .map(normalizeMarketIntelligenceItem)
        .filter((item) => item.slug !== normalizedArticle.slug);

      setArticle(normalizedArticle);
      setRelatedArticles(normalizedList.slice(0, 3));
      setError(null);
    } catch (err) {
      setArticle(null);
      setRelatedArticles([]);
      setError(err.response?.data?.error || 'Article not found');
    } finally {
      setLoading(false);
    }
  };

  const summaryBullets = useMemo(() => {
    if (!article) return [];

    if (Array.isArray(article.content_blocks) && article.content_blocks.length > 0) {
      const block = article.content_blocks[0];
      if (typeof block === 'string') return [block];
      if (block?.body) return [block.body];
    }

    if (article.subtitle) return [article.subtitle];
    if (article.excerpt) return [article.excerpt];
    return [];
  }, [article]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f7f4]">
        <div className="rounded-2xl bg-white px-6 py-5 shadow-sm">
          Loading market intelligence...
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f7f4]">
        <div className="text-center">
          <p className="mb-4 text-red-600">{error || 'Article not found'}</p>
          <button
            onClick={() => navigate('/market-intelligence')}
            className="rounded-full bg-[#f7911d] px-6 py-3 text-sm font-semibold text-white"
          >
            Back to Market Intelligence
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f7f4] text-[#0f172a]">
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[88rem] items-center justify-between px-5 py-4 lg:px-8">
          <button onClick={() => navigate('/')} className="flex items-center gap-3">
            <img src={logo} alt="Candid-I" className="h-8 w-auto" />
          </button>
          <nav className="flex items-center gap-4 text-sm font-medium text-slate-600">
            <Link to="/market-intelligence/archive" className="transition hover:text-[#f7911d]">
              Archive
            </Link>
            <Link to="/market-intelligence" className="transition hover:text-[#f7911d]">
              Hub
            </Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-[88rem] px-5 py-12 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.25fr_0.75fr]">
          <div>
            <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-slate-500">
              <span className="rounded-full bg-[#f7911d] px-3 py-1 text-white">
                {article.category}
              </span>
              <span>{formatMarketDate(article.published_at)}</span>
              <span>{article.read_time_minutes} min read</span>
              {article.video_duration_seconds ? (
                <span className="rounded-full bg-slate-100 px-3 py-1">
                  Video: {article.video_duration_seconds}s
                </span>
              ) : null}
            </div>

            <h1 className="mt-5 text-4xl font-semibold tracking-tight text-slate-950 md:text-6xl">
              {article.hero_headline || article.title}
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-600">
              {article.hero_supporting_line || article.subtitle || article.excerpt}
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/market-intelligence/briefing"
                className="rounded-full bg-[#f7911d] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200/60 transition hover:bg-[#e77e08]"
              >
                Join the market briefing
              </Link>
              <Link
                to="/market-intelligence/subscribe"
                className="rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-800 transition hover:border-[#f7911d] hover:text-[#f7911d]"
              >
                Subscribe
              </Link>
            </div>

            {summaryBullets.length > 0 && (
              <div className="mt-10 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                  Summary
                </div>
                <ul className="mt-4 space-y-3 text-sm leading-7 text-slate-600">
                  {summaryBullets.map((bullet) => (
                    <li key={bullet} className="flex gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-[#f7911d]" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="prose prose-slate mt-10 max-w-none rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
              <style>
                {`
                  .article-body h1, .article-body h2, .article-body h3 {
                    color: #0f172a !important;
                    font-weight: 700;
                    margin-top: 2rem;
                    margin-bottom: 1rem;
                  }
                  .article-body p, .article-body li {
                    color: #334155 !important;
                    line-height: 1.9;
                  }
                  .article-body a {
                    color: #f7911d !important;
                  }
                  .article-body blockquote {
                    border-left: 4px solid #f7911d;
                    padding-left: 1rem;
                    font-style: italic;
                    color: #475569;
                  }
                `}
              </style>
              <div className="article-body" dangerouslySetInnerHTML={{ __html: article.content }} />
            </div>
          </div>

          <aside className="space-y-6">
            <div className="overflow-hidden rounded-[2rem] bg-slate-950 p-3 shadow-xl">
              <div className="aspect-[4/3] overflow-hidden rounded-[1.6rem]">
                {renderMedia(article) || (
                  <div className="flex h-full items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 text-center text-white">
                    <div className="px-6">
                      <div className="text-xs uppercase tracking-[0.3em] text-[#f7911d]">Editorial asset</div>
                      <div className="mt-3 text-2xl font-semibold">Premium visual or video slot</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                Metadata
              </div>
              <div className="mt-4 space-y-3 text-sm text-slate-600">
                <div><span className="font-semibold text-slate-900">Author:</span> {article.author_name}</div>
                <div><span className="font-semibold text-slate-900">Role:</span> {article.author_role || 'Research Desk'}</div>
                <div><span className="font-semibold text-slate-900">Sector:</span> {(article.sector || []).join(', ') || article.category}</div>
                <div><span className="font-semibold text-slate-900">Region:</span> {(article.region || []).join(', ') || 'Global'}</div>
                <div><span className="font-semibold text-slate-900">Topics:</span> {(article.topics || []).join(', ') || 'General'}</div>
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
                Related insights
              </div>
              <div className="mt-4 space-y-4">
                {relatedArticles.slice(0, 3).map((item) => (
                  <Link
                    key={item.id}
                    to={`/market-intelligence/${item.slug}`}
                    className="block rounded-2xl bg-slate-50 p-4 transition hover:bg-orange-50"
                  >
                    <div className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">
                      {item.category}
                    </div>
                    <div className="mt-2 text-sm font-semibold text-slate-950">
                      {clampText(item.title, 70)}
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      {formatMarketDate(item.published_at)} - {item.read_time_minutes} min read
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default MarketIntelligenceArticle;
